import { useQuery } from '@tanstack/react-query';
import { formatPkr } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { KpiCard } from '../components/KpiCard.js';
import { LazyTripsPerHourChart } from '../components/LazyChart.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { StatusPill } from '../components/StatusPill.js';
import { listTrips, overview, type TripRow } from '../api/admin.js';

const recentColumns: Column<TripRow>[] = [
  { key: 'time', header: 'Requested', cell: (t) => new Date(t.requestedAt).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) },
  { key: 'rider', header: 'Rider', cell: (t) => <span className="font-medium">{t.rider.name}</span> },
  { key: 'route', header: 'Route', cell: (t) => `${t.pickupLabel} → ${t.dropLabel}` },
  { key: 'status', header: 'Status', cell: (t) => <StatusPill status={t.status} /> },
  { key: 'fare', header: 'Fare', align: 'right', cell: (t) => <span className="num font-semibold">{formatPkr(t.fareTotal)}</span> },
];

export function OverviewPage() {
  const overviewQ = useQuery({ queryKey: ['overview'], queryFn: overview, refetchInterval: 8000 });
  const recent = useQuery({ queryKey: ['admin-trips', 'recent'], queryFn: () => listTrips({}), refetchInterval: 10_000 });

  return (
    <Shell title="Overview" subtitle="Real-time platform health and today's totals.">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Active trips" value={overviewQ.data?.activeTrips ?? 0} hint="In flight right now" />
        <KpiCard label="Online drivers" value={overviewQ.data?.onlineDrivers ?? 0} hint="Approved + ready to drive" />
        <KpiCard label="Today's rides" value={overviewQ.data?.todayTrips ?? 0} hint="Requested since midnight" />
        <KpiCard label="Today's revenue" value={formatPkr(overviewQ.data?.todayRevenue ?? 0)} hint="Completed trips only" />
      </div>

      <div className="mt-6">
        <LazyTripsPerHourChart data={overviewQ.data?.tripsByHour ?? []} />
      </div>

      <section className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Recent activity</h2>
          <p className="text-xs text-slate-500">Last 100 trips</p>
        </div>
        <DataTable
          rows={(recent.data ?? []).slice(0, 12)}
          columns={recentColumns}
          rowKey={(t) => t.id}
          loading={recent.isLoading}
        />
      </section>
    </Shell>
  );
}
